import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DesignSystemComponent, ComponentCategory } from 'designSystem/types';
import { ComponentCard } from './components/ComponentCard';
import FilterPills from './FilterPills';
import SearchBar from './SearchBar';
import * as S from './styles';
import * as ComponentS from './components/styles';

// Map ComponentCategory to display name
const categoryDisplayNames: Record<ComponentCategory, string> = {
	button: 'Buttons',
	card: 'Cards',
	form: 'Forms',
	navigation: 'Navigation',
	'data-display': 'Data Display',
	other: 'Other',
};

const ComponentsView = () => {
	const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const { selectedCategories, searchQuery } = useSelector(
		(r: RootReducer) => r.mode.designSystemFilters.components
	);

	// Get available categories with counts
	const availableCategories = useMemo(() => {
		if (!designSystemContent?.components) return [];
		return (Object.keys(categoryDisplayNames) as ComponentCategory[]).map((category) => ({
			value: category,
			label: categoryDisplayNames[category],
			count: designSystemContent.components!.byCategory[category]?.length || 0,
		}));
	}, [designSystemContent?.components]);

	// Filter components based on selected categories and search query
	const componentsToDisplay = useMemo(() => {
		if (!designSystemContent?.components) return [];

		let components: DesignSystemComponent[] = [];

		// If categories selected, show union of all selected categories
		if (selectedCategories.length > 0) {
			const componentSets = selectedCategories.map(
				(cat) => designSystemContent.components!.byCategory[cat] || []
			);
			// Union: combine all sets and remove duplicates
			const componentMap = new Map<string, DesignSystemComponent>();
			componentSets.forEach((componentSet) => {
				componentSet.forEach((component) => {
					componentMap.set(component.id, component);
				});
			});
			components = Array.from(componentMap.values());
		} else {
			// No categories selected, show all
			components = designSystemContent.components.all || [];
		}

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			components = components.filter(
				(component) =>
					component.name.toLowerCase().includes(query) ||
					component.id.toLowerCase().includes(query) ||
					component.tagName.toLowerCase().includes(query)
			);
		}

		return components;
	}, [designSystemContent?.components, selectedCategories, searchQuery]);

	const rawCode = designSystemContent?.rawCode || {
		tokens: '',
		primitives: '',
		components: '',
	};

	return (
		<S.SectionBase>
			<S.SectionHeader>
				<S.SectionTitle>Components</S.SectionTitle>
				<SearchBar section="components" placeholder="Search components..." />
			</S.SectionHeader>
			<S.SectionContent>
				<FilterPills section="components" categories={availableCategories} />
				{componentsToDisplay.length === 0 ? (
					<p>
						No components found
						{selectedCategories.length > 0 && ` matching selected categories`}
						{searchQuery && ` matching "${searchQuery}"`}.
					</p>
				) : (
					<ComponentS.ComponentGrid>
						{componentsToDisplay.map((component) => (
							<ComponentCard
								key={component.id}
								component={component}
								selected={selectedComponentId === component.id}
								onClick={() =>
									setSelectedComponentId(component.id === selectedComponentId ? null : component.id)
								}
								rawCode={rawCode}
							/>
						))}
					</ComponentS.ComponentGrid>
				)}
			</S.SectionContent>
		</S.SectionBase>
	);
};

export default ComponentsView;
